/**
 * TeamConfigPage — Hub unificado de configuración del equipo.
 *
 * Reúne en una sola página (5 tabs) lo que antes estaban dispersos en
 * 4 rutas distintas: /team, /groups, /guards, /rules + sección Duplicados
 * de /settings. Las rutas viejas siguen funcionando como redirects al tab
 * correcto (ver App.js).
 *
 * Estructura:
 *   📋 Equipo       — TeamPage embebido
 *   👥 Grupos       — GroupsPage embebido
 *   🕒 Guardias     — GuardsPage (solo Round Robin) + AssignmentsCalendar
 *   🛡 Duplicados   — DuplicateDetectionCard
 *
 * Las reglas del pipeline viven en Oportunidades (solo admin).
 *
 * Para que TeamPage, GroupsPage, GuardsPage y RulesPage funcionen sin tocar
 * sus rutas viejas, los embebemos directamente como sub-componentes.
 */
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Users, UsersRound, Clock, Shield } from 'lucide-react';
import TeamPage from '@/pages/TeamPage';
import GroupsPage from '@/pages/GroupsPage';
import GuardsPage from '@/pages/GuardsPage';
import AssignmentsCalendar from '@/components/team-config/AssignmentsCalendar';
import DuplicateDetectionCard from '@/components/team-config/DuplicateDetectionCard';
import RespetarHorarioCard from '@/components/team-config/RespetarHorarioCard';
import VendedoresSinTelefonoAlert from '@/components/team-config/VendedoresSinTelefonoAlert';
import BigTooltip from '@/components/team-config/BigTooltip';

const TAB_DEFS = [
  { key: 'equipo', label: 'Equipo', icon: Users, tooltip: { title: 'Equipo', body: 'Gestiona los usuarios (vendedores, admins) que tienen acceso a tu CRM. Cada usuario aparece aquí cuando se le envía invitación.', useCase: 'Una agencia con 15 asesores ve aquí a todo el equipo, puede activar/desactivar, asignar permisos por módulo y enviar nuevas invitaciones.', academySearch: 'gestion equipo usuarios' } },
  { key: 'grupos', label: 'Grupos', icon: UsersRound, tooltip: { title: '¿Qué son los Grupos?', body: 'Los grupos te permiten organizar a tu equipo por marca, sucursal o especialidad. Cada grupo tiene su propia ruleta de asignación y reglas.', useCase: 'Una franquicia con 3 sucursales crea 3 grupos. Los leads de la sucursal Norte se reparten solo entre vendedores de ese grupo.', academySearch: 'grupos ruleta asignacion' } },
  { key: 'guardias', label: 'Guardias y Round Robin', icon: Clock, tooltip: { title: 'Roles de Guardia + Ruleta', body: 'Configura quién está de guardia en cada día/hora y cómo se rota la asignación. La ruleta automática Round Robin distribuye leads equitativamente entre vendedores activos.', useCase: 'Una agencia define que de lunes a viernes 9am-6pm están todos en ruleta, sábado solo 2 personas de guardia. Los leads se asignan según horario.', academySearch: 'guardias round robin ruleta' } },
  { key: 'duplicados', label: 'Detección de Duplicados', icon: Shield, tooltip: { title: 'Detección de Duplicados', body: 'Evita que el mismo prospecto entre 2 veces a tu pipeline desde distintas fuentes. Configurable por empresa, marca, producto o fuente.', useCase: 'Un lead se registra el lunes vía Facebook y el viernes llama por teléfono. El sistema detecta el duplicado y NO crea uno nuevo: actualiza/fusiona o bloquea según tu config.', academySearch: 'deteccion duplicados leads' } },
];

export default function TeamConfigPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'reglas' ? 'equipo' : (searchParams.get('tab') || 'equipo');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [allowedModules, setAllowedModules] = useState({});

  // Cargar módulos permitidos del plan
  useEffect(() => {
    api.get('/license/modules').then(r => {
      setAllowedModules(r.data?.allowed_modules || {});
    }).catch(() => setAllowedModules({}));
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'reglas') {
      navigate('/opportunities?seccion=reglas', { replace: true });
    }
  }, [searchParams, navigate]);

  // Sincronizar tab → URL para deep-linking
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  const visibleTabs = useMemo(() => {
    return TAB_DEFS.filter(t => {
      if (t.requiresModule && allowedModules[t.requiresModule] === false) return false;
      return true;
    });
  }, [allowedModules]);

  useEffect(() => {
    const keys = visibleTabs.map(t => t.key);
    if (keys.length && !keys.includes(activeTab)) setActiveTab(keys[0]);
  }, [visibleTabs, activeTab]);

  return (
    <div className="space-y-6" data-testid="team-config-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Team Config</h1>
        <p className="text-muted-foreground text-xs sm:text-sm mt-1 max-w-2xl">
          Hub central de tu equipo: usuarios, grupos, guardias y detección de duplicados. Quién recibe qué lead, en un solo lugar. Las reglas del pipeline están en Oportunidades (solo admin).
        </p>
      </div>

      {/* R5: banner de vendedores sin teléfono. Arriba de los tabs → visible desde CUALQUIER
          tab. Solo admins (lo gatea el 403 del endpoint) y solo si hay alguno; si no, no
          renderiza nada. */}
      <VendedoresSinTelefonoAlert onGoToTeam={() => setActiveTab('equipo')} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1F2833]/80 border border-white/5 mb-5 flex flex-wrap h-auto" data-testid="team-config-tabs">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="data-[state=active]:bg-cyan-400/15 data-[state=active]:text-cyan-400 text-xs gap-1.5"
                data-testid={`team-config-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
                <BigTooltip {...t.tooltip} side="right" />
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="equipo">{activeTab === 'equipo' && <TeamPage />}</TabsContent>
        <TabsContent value="grupos">{activeTab === 'grupos' && <GroupsPage />}</TabsContent>
        <TabsContent value="guardias">
          {activeTab === 'guardias' && (
            <div className="space-y-6">
              <RespetarHorarioCard />
              <AssignmentsCalendar />
              <Card className="bg-card/40 border-white/5">
                <CardContent className="p-5">
                  <GuardsPage />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent value="duplicados">
          {activeTab === 'duplicados' && <DuplicateDetectionCard allowedModules={allowedModules} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
